import { useAchievementTypes, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { Alert, EmptyIndicator, FlexContainer } from 'components/common';
import { Box, BoxBackLink, BoxRow } from 'components/common/box';
import {
  AchievementCompletionClosingApprovalHistory,
  AchievementCompletionItemsCleared,
  AchievementCompletionsCloseConversation,
  AchievementCompletionsSetConfidential,
  AchievementGroupOfUsersCompletionData,
  AchievementGroupsOfUsersGroupFillTemplate,
  AchievementGroupsOfUsersListActions
} from '.';

import { GetAchievementGroupCompletionItemsRes } from 'shared-types/responses';

interface Props {
  completionId: number;
}

const AchievementGroupsOfUsersGroup = ({ completionId }: Props) => {
  const {
    messages,
    pageState: { achievement },
    theme,
    user
  } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const { data, loading, updateData } =
    useApiData<GetAchievementGroupCompletionItemsRes>({
      onlyIf: Boolean(achievement.id && completionId),
      url: `/admin/achievements/${achievement.id}/group-completions/${completionId}/items`
    });

  const isCompletionClosed =
    data?.achievementData?.isClosed || data?.achievementData?.isInactive;

  const isSuperadmin =
    achievement.isOwnerOrSuperadmin || user?.roles.includes('superadmin');

  const canAnswerAndMark =
    isSuperadmin ||
    (achievement.isSupervisor && data?.isLoggedUserSupervisor) ||
    achievement.userAdminPermissions?.includes('manage-users');

  const doNotShowCloseConversation =
    !canAnswerAndMark ||
    data?.achievementData?.isInactive ||
    !achievement.allowClosingConversation ||
    (data?.achievementData && data?.achievementData?.status !== 'completed');

  return !data?.achievementData ? null : (
    <>
      <Box
        headerContent={
          <FlexContainer style={{ justifyContent: 'space-between' }}>
            <BoxBackLink simpleReturn={true} />

            {data.achievementData.status !== 'not started' ? (
              <AchievementGroupsOfUsersListActions
                groupCompletion={data}
                id="achievement-groups-of-users-group"
                isInDetailsPage={true}
                updateGroups={updateData}
              />
            ) : null}
          </FlexContainer>
        }
        loading={loading}
      >
        <AchievementGroupOfUsersCompletionData
          achievementData={data.achievementData}
          members={data.members}
        />
      </Box>

      <Box
        loading={loading}
        padding={0}
        style={{ marginTop: 25 }}
        title={
          <FlexContainer
            style={{ justifyContent: 'space-between', gap: 7, width: '100%' }}
          >
            <div style={{ display: 'flex', gap: 7 }}>
              <div>{achievementTypes[achievement.type].templateHeader}</div>
              <div
                style={{
                  color: theme.textColorLighter,
                  display: 'inline-block',
                  fontSize: '1.5rem',
                  fontWeight: 600
                }}
              >
                {data.achievementData.canSeeCompletionItems
                  ? `(${data.achievementData.numberOfCompletedTemplateItems}/${achievement.template.length})`
                  : ''}
              </div>
            </div>

            <AchievementCompletionsSetConfidential
              achievementId={achievement.id}
              achievementCompletionId={data.achievementData.id}
              disabled={!achievement.allowConfidential || !canAnswerAndMark}
              isConfidential={data.achievementData.isConfidential}
              isUserOrMember={data?.members?.some(
                (member) => member.id === user?.id
              )}
              updateAchievement={updateData}
              users={data.members}
            />
          </FlexContainer>
        }
      >
        {data.achievementData.completionItemsClearedDate ? (
          <AchievementCompletionItemsCleared
            date={data.achievementData.completionItemsClearedDate}
            type={achievement.type}
          />
        ) : data.achievementData.canSeeCompletionItems ? (
          <>
            {data.items.map((item, i) => (
              <AchievementGroupsOfUsersGroupFillTemplate
                achievement={achievement}
                canAnswer={!isCompletionClosed && canAnswerAndMark}
                canMarkComplete={!isCompletionClosed && canAnswerAndMark}
                completionId={completionId}
                key={item.id}
                item={item}
                itemIndex={i + 1}
                updateItems={updateData}
              />
            ))}

            {doNotShowCloseConversation ? null : (
              <BoxRow>
                {data.achievementData.isClosed && (
                  <Alert
                    type="warning-light"
                    text={messages.achievementConversationClosed}
                  />
                )}

                <AchievementCompletionsCloseConversation
                  achievement={achievement}
                  achievementData={data.achievementData}
                  achievementId={achievement.id}
                  completionIdentifier={completionId}
                  isUserOrMember={data?.members?.some(
                    (member) => member.id === user?.id
                  )}
                  updateData={updateData}
                />

                <AchievementCompletionClosingApprovalHistory
                  achievement={achievement}
                  achievementData={data.achievementData}
                />
              </BoxRow>
            )}
          </>
        ) : (
          <EmptyIndicator
            description={messages.achievementCompletionConfidentialInfo}
            icon="lock"
            title={messages.commonConfidential}
          />
        )}
      </Box>
    </>
  );
};

export default AchievementGroupsOfUsersGroup;
